import { APPKEYOBJ } from '@feedback/utils/CONST'
import LANGUAGE, {
  LanguageShortKeyMaps,
  SHORT_KEY_APPIDS
} from '@feedback/utils/LANGUAGE'
import sha256 from 'js-sha256'
import moment from 'moment'
import { reportApi, trackingApi } from '../services/index'
import { getVestSiteSlugPayload, isVestAppid } from '@feedback/utils/siteConfig'

/**
 * 该 appid 是否走短键（LanguageShortKeyMaps）解析路径。
 * 短键家族白名单命中，或马甲站主体 appid（cm1017-b25 / cm1017-sweet）。
 * getTextByKey / getLangKey 共用，消除历史上两处漂移的重复清单。
 */
const usesShortLocaleKey = (appid) =>
  SHORT_KEY_APPIDS.includes(appid) || isVestAppid(appid)

export const timestampToDate = (timestamp) => {
  return new Date(parseInt(timestamp, 10) * 1000)
    .toString()
    .replace(/GMT.*?$/, '')
}

function ksort(o) {
  let sorted = {}
  let keys = Object.keys(o)
  keys.sort()
  keys.forEach((key) => {
    sorted[key] = o[key]
  })
  return sorted
}
/**
 * 签名规则地址：http://showdoc.stardustworld.cn/web/#/8?page_id=2266
 */
export const sign = ({ nonce, ts, otherParams = {} }) => {
  let headerFromApp = sessionStorage.getItem('headerFromApp')
  if (!headerFromApp) {
    return ''
  }
  headerFromApp = JSON.parse(headerFromApp)
  const params = { ...otherParams }
  const { appid, did, channel, language, dlevel, version } = headerFromApp
  const appKey = APPKEYOBJ[appid]
  params.appid = appid
  params.ts = ts
  params.nonce = nonce
  params.did = did
  params.channel = channel
  params.language = language
  params.dlevel = dlevel
  params.version = version
  params['package-name'] = headerFromApp['package-name']
  const handleParams = ksort(params)
  let str = ''
  let idx = 0
  for (const key in handleParams) {
    let val = handleParams[key]
    if (val === '' || val === undefined) {
      continue
    }
    if (typeof val === 'object') {
      val = JSON.stringify(val)
    }
    str += `${idx ? '&' : ''}${key}=${val}`
    ++idx
  }
  // return sha256.hmac(str, appKey)
  return sha256.hmac(appKey, str)
}

export const getTextByKey = (key) => {
  if (!key) {
    return ''
  }
  const paramsObj = sessionStorage.getItem('headerFromApp')
  const handleParamsObj = paramsObj ? JSON.parse(paramsObj) : {}
  if (usesShortLocaleKey(handleParamsObj?.appid)) {
    const languageId =
      LanguageShortKeyMaps?.[handleParamsObj?.language] ||
      Object.keys(LANGUAGE)?.find?.((lang) =>
        lang?.includes?.(handleParamsObj?.language)
      )
    return LANGUAGE?.[languageId]?.[key] || LANGUAGE?.['en-US']?.[key] || key
  }
  return (
    LANGUAGE?.[handleParamsObj?.language]?.[key] ||
    LANGUAGE?.['en-US']?.[key] ||
    key
  )
}
export const getLangKey = () => {
  const paramsObj = sessionStorage.getItem('headerFromApp')
  const handleParamsObj = paramsObj ? JSON.parse(paramsObj) : {}
  let language = 'en-US' // 默认多语言为英语
  if (handleParamsObj.language && LANGUAGE[handleParamsObj.language]) {
    // 如果该语言本地有对应的语言对象
    language = handleParamsObj.language
  }
  if (
    usesShortLocaleKey(handleParamsObj?.appid) &&
    LanguageShortKeyMaps?.[handleParamsObj?.language]
  ) {
    language = LanguageShortKeyMaps?.[handleParamsObj?.language]
  }
  return language
}

export const getTimeFormat = (value) => {
  return moment(value).format('MM-DD-YYYY HH:mm')
}

export const subscriptionNetworkStatus = (fn) => {
  let online = false
  const connection =
    navigator?.connection ||
    navigator?.mozConnection ||
    navigator?.webkitConnection

  window.addEventListener('offline', (event) => {
    online = event?.currentTarget?.navigator?.onLine
  })
  window.addEventListener('online', (event) => {
    online = event?.currentTarget?.navigator?.onLine
  })
  connection?.addEventListener?.('change', (event) => {
    if (!online) {
      fn('offline')
    } else if (event?.currentTarget?.type === 'wifi') {
      fn('wifi')
    } else {
      fn(event?.currentTarget?.effectiveType || 'unknown')
    }
  })

  if (!navigator?.onLine) {
    fn('offline')
  } else if (connection?.type === 'wifi') {
    fn('wifi')
  } else {
    fn(connection?.effectiveType || 'unknown')
  }
}

export function getTimezoneOffsetString(date) {
  const tzo = -date.getTimezoneOffset()
  const dif = tzo >= 0 ? '+' : '-'
  const pad = function (num) {
    return (num < 10 ? '0' : '') + num
  }
  return (
    dif + pad(Math.floor(Math.abs(tzo) / 60)) + ':' + pad(Math.abs(tzo) % 60)
  )
}

// 公共上报函数，只需要传position/button/parm1三个参数就可以了，公共参数已设置好
// 马甲站自动附带 site_slug；既有 app 不带该字段
export const reportFn = async (paramsObj) => {
  let headerFromApp = sessionStorage.getItem('headerFromApp')
  if (!headerFromApp) {
    return ''
  }
  headerFromApp = JSON.parse(headerFromApp)
  const { appid, did, channel, uuid } = headerFromApp
  const params = {
    uuid,
    appid,
    channel,
    adid: did,
    ...paramsObj,
    ...getVestSiteSlugPayload()
  }

  if (['cm1006', 'cm1007']?.includes(appid)) return
  if (['cm1009', 'cm1012', 'cm1015', 'cm1017']?.includes(appid)) {
    params.parm10 = headerFromApp?.from || ''
  }

  let result = await reportApi(params)
  if (!result || result.code !== 0) {
    console.log('report error')
  }
}

// 新的上报函数，适配 J项目、AVG 4.0
// 马甲站自动附带 site_slug；既有 app 不带该字段
export const tracking = async (params) => {
  if (!sessionStorage.getItem('headerFromApp')) {
    return ''
  }

  let network = 0
  const networkCode = {
    unknown: 0,
    wifi: 1,
    '2g': 2,
    '3g': 3,
    '4g': 4,
    '5g': 5
  }
  subscriptionNetworkStatus((status) => {
    network = Object.keys(networkCode)?.includes(status)
      ? networkCode[status]
      : networkCode?.['unknown']
  })

  const trackingData = JSON.parse(sessionStorage.getItem('tracking-data'))
  const data = {
    _event_name: 'm_user_click',
    ...trackingData,
    _scene_name: 'hall_scene',
    _page_name: 'Panel_Main',
    _click_event_id: '',
    _click_event_name: 'h5_feedback_click',
    _os_timestamp: Math.floor(Date.now() / 1000),
    _os_timezone_offset: getTimezoneOffsetString(new Date()),
    _device_network_type: network,
    ...params,
    ...getVestSiteSlugPayload()
  }

  return await trackingApi(data)
}

export const initFromEntries = () => {
  Object.fromEntries = (arrArg) => {
    // URLSearchParams
    if (Object.prototype.toString.call(arrArg) === '[object URLSearchParams]') {
      const resMap = []
      for (const key of arrArg.keys()) {
        const value = arrArg.getAll(key)
        resMap[key] = value[value.length - 1]
      }
      return resMap
    }
    // Map
    if (Object.prototype.toString.call(arrArg) === '[object Map]') {
      const resMap = {}
      for (const key of arrArg.keys()) {
        resMap[key] = arrArg.get(key)
      }
      return resMap
    }
    // Array
    if (Array.isArray(arrArg)) {
      const resArr = {}
      arrArg.forEach(([key, value]) => {
        resArr[key] = value
      })
      return resArr
    }
    throw new Error('Uncaught TypeError: argument is not iterable')
  }
}

export function objectToQueryString(obj) {
  return Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&')
}

export { default as purchaseLinkParams } from './purchaseLinkParams'
